import { WidgetAttachment } from './common';
import { PRODUCT_DISCOUNT_TYPE_V2, ProductItemV2 } from '../../product';
import { ValueOf } from '../../common/utils';
import {
  ProductWidgetsCreateRequestWidgetAttachmentDto,
  ProductWidgetsCreateRequestWidgetDto,
  ProductWidgetsCreateRequestWidgetDtoLayoutTypeEnum,
  ProductWidgetsCreateRequestWidgetItemProductDto,
  ProductWidgetsCreateRequestWidgetProductLayoutDto,
  ProductWidgetsFindOneResponseWidgetDto,
} from '../../api/widgets';
import { EventItemV2 } from '../../event';
import { WIDGET_HOME_TYPE_V2, WIDGET_TYPE_V2 } from '../common';
import { Attachment } from '../../../components/input/ImageAndVideoUploadV2/types';
import { DateRageType } from '../../common/date';
import { ATTACHMENT_TYPE } from '../../../components/input/ImageAndVideoUploadV2/constants';

export const PRODUCT_WIDGET_LAYOUT_TYPE_V2 = {
  GRID_2X2: 'grid2x2',
  GRID_3X2: 'grid3x2',
  SWIPE_BIG_1XN: 'swipeBig1xn',
  SWIPE_SMALL_1XN: 'swipeSmall1xn',
  GALLERY_3X3: 'gallery3x3',
} as const;

export type PRODUCT_WIDGET_LAYOUT_TYPE_V2 = ValueOf<typeof PRODUCT_WIDGET_LAYOUT_TYPE_V2>;

export const PRODUCT_WIDGET_ATTACHMENT_TYPE_V2 = {
  //기획전 연결 X 일때 이동하는 위젯 상세 페이지 이미지
  DETAIL: 'detail',
  //스와이프 1XN 레이아웃일때 상단 이미지
  TOP: 'top',
  VIDEO: 'video',
} as const;

export type PRODUCT_WIDGET_ATTACHMENT_TYPE_V2 = ValueOf<typeof PRODUCT_WIDGET_ATTACHMENT_TYPE_V2>;

export const PRODUCT_CONDITION_TYPE_V2 = {
  SALES: 'salesPrice',
  DISCOUNT: 'discountPrice',
} as const;

export type PRODUCT_CONDITION_TYPE_V2 = ValueOf<typeof PRODUCT_CONDITION_TYPE_V2>;

export type ProductWidgetAttachment = WidgetAttachment<PRODUCT_WIDGET_ATTACHMENT_TYPE_V2> & { no?: number };

export type ProductConditionV2 = {
  //상품 위젯 조건 타입( salesPrice :실판매가 , discountPrice :할인가 )
  type: PRODUCT_CONDITION_TYPE_V2;
  //상품 위젯 조건 할인 타입( amount :금액할인 , percent : %할인 )
  discountType: PRODUCT_DISCOUNT_TYPE_V2;
  //상품 위젯 조건 최대값
  maximumValue: number;
  //상품 위젯 조건 최소값
  minimumValue: number;
};

export type WidgetProductItem = ProductItemV2 & { no?: number };

export class ProductWidgetDetailV2 {
  type: typeof WIDGET_TYPE_V2.PRODUCT;
  isProductRecommend?: boolean;
  layoutType: PRODUCT_WIDGET_LAYOUT_TYPE_V2;
  mainTitle: string;
  subTitle?: string;
  memo?: string;
  exposurePeriod: DateRageType;
  buttonText?: string;
  event?: EventItemV2;
  isEventConnect: boolean;
  currentExposurePage?: string;
  detailImages: Attachment[];
  widgetItemProducts?: WidgetProductItem[];
  exposureWidgetItemProducts?: WidgetProductItem[];
  topImage?: Attachment[];
  isRandomItem?: boolean;
  exposureItemCount?: number;

  constructor(detail: ProductWidgetsFindOneResponseWidgetDto) {
    this.type = WIDGET_TYPE_V2.PRODUCT;
    this.isProductRecommend = detail.isProductRecommend;
    this.layoutType = detail.layoutType;
    this.mainTitle = detail.mainTitle;
    this.subTitle = detail.subTitle;
    this.memo = detail.memo;
    this.exposurePeriod = {
      startDate: detail.exposureStartDate,
      endDate: detail.exposureEndDate,
    };
    this.buttonText = detail.buttonText;
    this.event = detail.event && new EventItemV2(detail.event);
    this.isEventConnect = !!detail.event;
    this.currentExposurePage = detail.home?.type === WIDGET_HOME_TYPE_V2.MAIN ? '메인 페이지' : detail.home?.title;
    this.detailImages = detail.widgetAttachments.filter(
      (attachment) =>
        attachment.type === PRODUCT_WIDGET_ATTACHMENT_TYPE_V2.DETAIL ||
        attachment.type === PRODUCT_WIDGET_ATTACHMENT_TYPE_V2.VIDEO,
    );
    this.widgetItemProducts = detail.widgetItemProducts.map((item) => new ProductItemV2(item.product));
    this.exposureWidgetItemProducts = detail.widgetItemProducts
      .filter((product) => product.no)
      .sort((a, b) => {
        if (a.no === undefined || b.no === undefined) return 0;
        return a.no - b.no;
      })
      .map((item) => new ProductItemV2(item.product));
    this.topImage = detail.widgetAttachments.filter(
      (attachment) => attachment.type === PRODUCT_WIDGET_ATTACHMENT_TYPE_V2.TOP,
    );
    this.isRandomItem = detail.widgetProductLayout.isRandomItem;
    this.exposureItemCount = detail.widgetProductLayout.exposureItemCount;
  }
}

export class ProductWidgetCreatePayloadV2 implements ProductWidgetsCreateRequestWidgetDto {
  mainTitle: string;
  subTitle: string;
  memo: string;
  buttonText: string;
  exposureStartDate: string | null;
  exposureEndDate: string | null;
  isProductRecommend: boolean;
  eventId?: number;
  layoutType: ProductWidgetsCreateRequestWidgetDtoLayoutTypeEnum;
  widgetItemProducts: Array<ProductWidgetsCreateRequestWidgetItemProductDto>;
  widgetAttachments: Array<ProductWidgetsCreateRequestWidgetAttachmentDto>;
  widgetProductLayout: ProductWidgetsCreateRequestWidgetProductLayoutDto;
  constructor(detail: ProductWidgetDetailV2) {
    //서버는 width,height,url이 필수값이므로 없는 이미지 필터링
    const detailImages =
      detail.detailImages
        ?.filter((image) => image.width && image.height && image.url)
        .map((image, idx) => ({
          ...image,
          no: idx + 1,
          type:
            image.type === ATTACHMENT_TYPE.VIDEO
              ? PRODUCT_WIDGET_ATTACHMENT_TYPE_V2.VIDEO
              : PRODUCT_WIDGET_ATTACHMENT_TYPE_V2.DETAIL,
        })) || [];

    const topImageItem = detail.topImage?.[0];

    const topImage =
      topImageItem?.width && topImageItem?.height && topImageItem?.url
        ? [{ ...topImageItem, type: PRODUCT_WIDGET_ATTACHMENT_TYPE_V2.TOP }]
        : [];

    this.mainTitle = detail.mainTitle;
    this.subTitle = detail.subTitle || '';
    this.memo = detail.memo || '';
    this.buttonText = detail.buttonText || '';
    this.exposureStartDate = detail.exposurePeriod.startDate && new Date(detail.exposurePeriod.startDate).toISOString();
    this.exposureEndDate = detail.exposurePeriod.endDate && new Date(detail.exposurePeriod.endDate).toISOString();
    this.isProductRecommend = !!detail.isProductRecommend;
    this.eventId = detail.event?.id;
    this.layoutType = detail.layoutType;
    this.widgetItemProducts =
      detail.widgetItemProducts?.map((item) => {
        const no = detail.exposureWidgetItemProducts?.findIndex((exposure) => exposure.id === item.id);
        return no !== undefined && no !== -1 ? { productId: item.id, no: no + 1 } : { productId: item.id };
      }) || [];
    this.widgetAttachments = [
      ...detailImages,
      ...topImage,
    ] as ProductWidgetsCreateRequestWidgetDto['widgetAttachments'];
    this.widgetProductLayout = {
      isRandomItem: detail.isRandomItem || false,
      exposureItemCount: detail.exposureItemCount || 0,
    };
  }
}
